import * as Sentry from "@sentry/nextjs";
import { getTokenRefreshFailure } from "queries/lib/rocky-api-error-handler";
import { isPageUnloading } from "ui/lib/page-unloading";

// SWR retries a failed key on exponential backoff indefinitely and fires
// onError on every attempt, so report once per endpoint per streak; a key
// re-arms on success or after a cooldown. The user_visible tag bypasses the
// beforeSend network-error filter — a failed read is what the user sees.
const failingKeys = new Set<string>();
const FAILING_KEY_COOLDOWN_MS = 5 * 60_000;

// bootcamps/<uuid>/calendar?start=… → bootcamps/:id/calendar, so the tag and
// fingerprint group by route rather than by team/workout id.
const normalizeEndpoint = (key: string) =>
  key
    .split("?")[0]
    .replace(/^\/+/, "")
    .split("/")
    .map((seg) => (/^\d+$|^[0-9a-f-]{20,}$/i.test(seg) ? ":id" : seg))
    .join("/");

export const clearFailingKey = (key: string) => {
  failingKeys.delete(normalizeEndpoint(key));
};

export const reportFetchError = (
  error: any,
  key: string,
  { hasData = false }: { hasData?: boolean } = {}
) => {
  const status = error?.response?.status;
  const refreshMode = getTokenRefreshFailure(error)?.mode;
  // A request the browser cancelled because the page is going away never
  // rendered anything, so it is not a failure the user saw. That is either
  // the read itself (no response) or the token refresh it was queued behind
  // (the interceptor re-rejects the original 401 tagged "network").
  // Not superseded by the same check in sentryBeforeSend: that one bails on
  // anything carrying a `response`, so it cannot cover the refresh case, which
  // is a 401 with one. This also returns before `failingKeys`, so a cancelled
  // read doesn't burn the endpoint's cooldown slot — beforeSend runs too late
  // for that, at capture time.
  if (isPageUnloading() && (status === undefined || refreshMode === "network")) {
    return;
  }
  // A background revalidation (focus, retry) that dies on the network leaves
  // the cached data on screen and SWR retries it, so the user saw nothing.
  // Only transport failures qualify: a response mapper throwing on a bad body
  // also has no status, and that is a bug worth seeing.
  const isTransportFailure =
    status === undefined && (error?.isAxiosError || error?.code === "ERR_NETWORK");
  if (hasData && isTransportFailure) return;
  // A 401 here means the interceptor's refresh already gave up. "rejected" is
  // Rocky expiring a stale session — routine — but "missing"/"network" are
  // the silent-bail modes worth seeing.
  if (status === 401 && refreshMode === "rejected") return;
  const endpoint = normalizeEndpoint(key);
  if (failingKeys.has(endpoint)) return;
  failingKeys.add(endpoint);
  setTimeout(() => failingKeys.delete(endpoint), FAILING_KEY_COOLDOWN_MS);
  Sentry.captureException(error, {
    tags: { source: "useRequest", endpoint, user_visible: true },
    fingerprint: ["useRequest", endpoint, String(status ?? "no-response")],
    extra: { status, key, refreshMode, responseData: error?.response?.data },
  });
};
